/**
 * Agent hotel flow — re-exports pipeline entry points.
 * @see utils/pricing/agentHotelPricingPipeline.js
 */

export {
  coalesceHotelHrCommissionPercent,
  coalesceFixedHotelCommission,
  roundHotelSearchMoney2,
  normalizeAgentHotelPricingInput,
  resolveAgentHotelPricing,
  resolveAgentHotelSupplierPriceIls,
  buildHotelAgentFxFromCurrencyRate,
  runAgentHotelPricingPipeline,
  formatResolvedAgentHotelPriceLine,
} from "./pricing/agentHotelPricingPipeline";

import {
  buildHotelAgentFxFromCurrencyRate,
  runAgentHotelPricingPipeline,
} from "./pricing/agentHotelPricingPipeline";

/** Merge AppContext FX into agent hotel search pricing params (USD clamp needs supplier ILS). */
export function mergeAgentHotelSearchFxParams(params, fxContext = {}) {
  const { currencyRate, usdToIls, ilsToUsd } = fxContext;
  const built = buildHotelAgentFxFromCurrencyRate(
    currencyRate ?? params?.currencyRate
  );
  return {
    ...params,
    currencyRate: currencyRate ?? params?.currencyRate,
    usdToIls: usdToIls ?? params?.usdToIls ?? built.usdToIls,
    ilsToUsd: ilsToUsd ?? params?.ilsToUsd ?? built.ilsToUsd,
  };
}

/**
 * @param {object} params
 * @param {number|null|undefined} params.apiPrice
 * @param {string|null|undefined} params.transactionCurrency
 * @param {string|null|undefined} params.hotelSearchResultsCurrency
 * @param {string|null|undefined} params.hotelSearchListDisplayCurrency
 * @param {unknown} params.hotelHrCommissionPercentage
 * @param {unknown} params.fixedHotelCommission
 * @param {number|null|undefined} [params.supplierPriceIls]
 * @param {(n: number, fixed?: boolean) => number|null|undefined} [params.usdToIls]
 * @param {(n: number, fixed?: boolean) => number|null|undefined} [params.ilsToUsd]
 * @param {{ USD?: unknown }|null|undefined} [params.currencyRate]
 * @returns {object}
 */
export function computeAgentHotelFlowPriceVm(params) {
  const merged = mergeAgentHotelSearchFxParams(params);
  const {
    apiPrice,
    transactionCurrency,
    hotelSearchResultsCurrency,
    hotelSearchListDisplayCurrency,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
    pricingAuditMeta,
    supplierPriceIls,
    usdToIls,
    ilsToUsd,
    currencyRate,
    entitlementsStatus,
  } = merged;
  return runAgentHotelPricingPipeline({
    sourceType: "agent_hotel_flow",
    rawData: {
      apiPrice,
      transactionCurrency,
      hotelSearchResultsCurrency,
      hotelSearchListDisplayCurrency,
      hotelHrCommissionPercentage,
      fixedHotelCommission,
      supplierPriceIls,
      usdToIls,
      ilsToUsd,
      currencyRate,
      entitlementsStatus,
      ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
        ? { pricingAuditMeta }
        : {}),
    },
  });
}

/**
 * תוצאות חיפוש סוכן: מטבע עסקה = מטבע סט התוצאות (אין שדה נפרד בשורה).
 */
export function computeAgentHotelSearchResultPriceVm({
  apiPrice,
  resultsDatasetCurrency,
  displayCurrency,
  hotelHrCommissionPercentage,
  fixedHotelCommission,
  pricingAuditMeta,
  supplierPriceIls,
  usdToIls,
  ilsToUsd,
  currencyRate,
  entitlementsStatus,
}) {
  return computeAgentHotelFlowPriceVm({
    apiPrice,
    transactionCurrency: resultsDatasetCurrency,
    hotelSearchResultsCurrency: resultsDatasetCurrency,
    hotelSearchListDisplayCurrency: displayCurrency,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
    pricingAuditMeta,
    supplierPriceIls,
    usdToIls,
    ilsToUsd,
    currencyRate,
    entitlementsStatus,
  });
}
